import React, { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowUpRight, Phone, Mail, MapPin, Loader2 } from "lucide-react";
import { submitQuote } from "@/lib/api";

const initial = { name: "", email: "", phone: "", address: "", service: "", message: "" };

export default function Contact({ site }) {
    const [form, setForm] = useState(initial);
    const [loading, setLoading] = useState(false);

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!form.name || !form.email || !form.phone) {
            toast.error("Name, email and phone are required.");
            return;
        }
        setLoading(true);
        try {
            await submitQuote(form);
            toast.success("Estimate request sent. We’ll be in touch shortly.");
            setForm(initial);
        } catch (err) {
            toast.error("Something went wrong. Please call us at " + site.phone);
        } finally {
            setLoading(false);
        }
    };

    const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
    const phoneTel = site.phone_raw || site.phone.replace(/\s+/g, "");

    return (
        <section
            id="contact"
            data-testid="contact-section"
            className="relative bg-ink-900 py-24 md:py-36"
        >
            <div className="mx-auto max-w-[1400px] px-5 md:px-10">
                <div className="grid grid-cols-12 gap-8 md:gap-14">
                    {/* Left: Massive type */}
                    <div className="col-span-12 lg:col-span-5">
                        <div className="font-mono-label text-lime-signal mb-4">07 — Free Estimate</div>
                        <motion.h2
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.8 }}
                            className="font-display uppercase leading-[0.85] text-6xl md:text-7xl lg:text-[9vw]"
                        >
                            <span className="text-white">Let&apos;s</span>
                            <br />
                            <span className="text-lime-signal">talk.</span>
                        </motion.h2>
                        <p className="mt-6 text-white/60 leading-relaxed max-w-md">
                            Send the form or reach us directly — we respond within one business day.
                        </p>

                        <ul className="mt-10 space-y-4 font-mono-label">
                            <li>
                                <a href={`tel:${phoneTel}`} data-testid="contact-phone" className="flex items-center gap-3 text-white hover:text-lime-signal">
                                    <Phone size={14} /> {site.phone}
                                </a>
                            </li>
                            <li>
                                <a href={`mailto:${site.email}`} data-testid="contact-email" className="flex items-center gap-3 text-white/80 hover:text-lime-signal">
                                    <Mail size={14} /> {site.email}
                                </a>
                            </li>
                            <li className="flex items-center gap-3 text-white/60">
                                <MapPin size={14} /> {site.address}
                            </li>
                        </ul>
                    </div>

                    {/* Right: Form */}
                    <motion.form
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.8, delay: 0.1 }}
                        onSubmit={onSubmit}
                        data-testid="contact-form"
                        className="col-span-12 lg:col-span-7 card-tactical p-6 md:p-10"
                    >
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                            <Field label="Name" htmlFor="name">
                                <input id="name" data-testid="input-name" value={form.name} onChange={update("name")} required
                                    className="input-shell" placeholder="First & last" />
                            </Field>
                            <Field label="Phone" htmlFor="phone">
                                <input id="phone" data-testid="input-phone" value={form.phone} onChange={update("phone")} required
                                    className="input-shell" placeholder="256 555 0000" />
                            </Field>
                            <Field label="Email" htmlFor="email" full>
                                <input id="email" type="email" data-testid="input-email" value={form.email} onChange={update("email")} required
                                    className="input-shell" placeholder="you@email.com" />
                            </Field>
                            <Field label="Property address" htmlFor="address" full>
                                <input id="address" data-testid="input-address" value={form.address} onChange={update("address")}
                                    className="input-shell" placeholder="Street, city" />
                            </Field>
                            <Field label="Service needed" htmlFor="service" full>
                                <select id="service" data-testid="input-service" value={form.service} onChange={update("service")} className="input-shell">
                                    <option value="">Select a service…</option>
                                    {site.services.map((s) => (
                                        <option key={s.id} value={s.title}>{s.title}</option>
                                    ))}
                                    <option value="Multiple services">Multiple services</option>
                                </select>
                            </Field>
                            <Field label="Message" htmlFor="message" full>
                                <textarea id="message" data-testid="input-message" value={form.message} onChange={update("message")}
                                    rows={4} className="input-shell resize-none" placeholder="Lot size, timing, notes…" />
                            </Field>
                        </div>

                        <div className="mt-8 flex flex-wrap items-center justify-between gap-4">
                            <p className="font-mono-label text-white/40 max-w-xs">
                                By submitting you agree to be contacted about your estimate.
                            </p>
                            <button
                                type="submit"
                                data-testid="contact-submit"
                                disabled={loading}
                                className="btn-signal disabled:opacity-60"
                            >
                                {loading ? <Loader2 className="animate-spin" size={18} /> : <ArrowUpRight size={18} />}
                                {loading ? "Sending…" : "Send request"}
                            </button>
                        </div>
                    </motion.form>
                </div>
            </div>

            {/* Inline style for input shell */}
            <style>{`
                .input-shell {
                    width: 100%; background: #0A0A0A; color: #fff;
                    border: 1px solid rgba(255,255,255,0.12);
                    padding: 14px 16px; border-radius: 10px;
                    font-size: 0.95rem; outline: none;
                    transition: border-color 0.2s, background-color 0.2s;
                }
                .input-shell::placeholder { color: rgba(255,255,255,0.35); }
                .input-shell:focus { border-color: #C6FF3D; background: #0e0e0e; }
                select.input-shell { appearance: none; background-image: linear-gradient(45deg, transparent 50%, #C6FF3D 50%), linear-gradient(135deg, #C6FF3D 50%, transparent 50%); background-position: calc(100% - 20px) 50%, calc(100% - 14px) 50%; background-size: 6px 6px, 6px 6px; background-repeat: no-repeat; padding-right: 36px; }
            `}</style>
        </section>
    );
}

function Field({ label, htmlFor, children, full }) {
    return (
        <label htmlFor={htmlFor} className={`block ${full ? "md:col-span-2" : ""}`}>
            <span className="font-mono-label text-white/50 block mb-2">{label}</span>
            {children}
        </label>
    );
}
